import { Badge, Box, Button, Group, Stack, Text } from '@mantine/core'
import { modals } from '@mantine/modals'
import { IconExternalLink, IconPlus } from '@tabler/icons-react'
import type { MRT_ColumnDef } from 'mantine-react-table'

import { Table } from '@/components/Table'
import { NESTED_MODAL_Z_INDEX } from '@/configs/theme'
import { theme } from '@/configs/theme'
import useCheckMobileScreen from '@/hooks/useCheckMobileScreen'
import { useGlobalState } from '@/hooks/useGlobalState'
import type { CMFormSection } from '@/screens/ContentModuleFormScreen/ContentModuleFormScreen'
import type { ResourceModalProps } from '@/screens/ContentModuleFormScreen/ResourceModal'
import { ResourceModal } from '@/screens/ContentModuleFormScreen/ResourceModal'
import type { GetResourcesResponse } from '@/services/api/api.types'
import { castResourceToZodForm } from '@/utils/contentModuleForm'

interface ResourcesTableProps extends CMFormSection {
  type: 'cm' | 'step'
  disabled?: boolean
  title?: string
}

export function ResourcesTable(props: CMFormSection & ResourcesTableProps) {
  const { configStore } = useGlobalState()
  const isMobile = useCheckMobileScreen()

  function openResourceModal(isNew: boolean) {
    props.openModal?.({
      title: `${isNew ? 'New' : 'Edit'} Supplemental Resource`,
      Component: ResourceModal,
      ComponentProps: { type: props.type } satisfies ResourceModalProps,
    })
  }

  function stageResource(resource?: GetResourcesResponse['0']) {
    const formValues = castResourceToZodForm(resource)

    if (!resource) {
      formValues.type = 'url'
    }

    props.resourceForm.setValues(formValues)
    props.resourceForm.resetDirty()
    props.resourceForm.resetTouched()
  }

  function deleteResource(resource: GetResourcesResponse['0']) {
    const idParams = props.type === 'cm' ? { task_id: props.taskId! } : { step_id: props.stepId! }

    modals.openConfirmModal({
      title: 'Delete Supplemental Resource?',
      centered: true,
      children: <Text size="sm">{`Are you sure you want to remove this resource from? This can't be undone.`}</Text>,
      labels: { confirm: 'Delete Resource', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      groupProps: { mt: 'xl' },
      zIndex: NESTED_MODAL_Z_INDEX,
      onCancel: () => console.log('Cancel'),
      onConfirm: () => props.resourceDeleteMutation?.mutate({ resource_id: resource.resourceId, ...idParams }),
    })
  }

  const stepResources =
    props.type === 'step' && props.stepId
      ? props.stepsQuery.data?.find(step => step.stepId === props.stepId)?.resources
      : []

  const data = props.type === 'cm' ? props.cmResourcesQuery.data : stepResources

  function generatePreviewHtml(title: string | undefined, content: string) {
    const styles = `
      body {
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        line-height: 1.6;
        margin: 0;
        padding: 2rem;
        max-width: 900px;
        margin: 0 auto;
      }
      pre {
        white-space: pre-wrap;
        word-wrap: break-word;
      }
      .title {
        margin-bottom: 2rem;
        color: #1a1b1e;
      }
      .content {
        background: white;
        padding: 2rem;
        border-radius: 8px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      }
    `

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${title || 'Text Preview'}</title>
          <style>${styles}</style>
        </head>
        <body>
          <h1 class="title">${title || 'Text Preview'}</h1>
          <div class="content">
            <pre>${content}</pre>
          </div>
        </body>
      </html>
    `
  }

  return (
    <Stack gap="xs" mt={props.title ? undefined : '-xl'}>
      <Group justify={props.title ? 'space-between' : 'flex-end'} wrap="wrap" gap="sm">
        {props.title && (
          <Text size="lg" fw={600}>
            {props.title}
          </Text>
        )}
        <Button
          variant="light"
          size="md"
          radius="md"
          leftSection={<IconPlus size={16} />}
          onClick={() => {
            stageResource()
            openResourceModal(true)
          }}
          disabled={props.disabled}>
          Add Resource
        </Button>
      </Group>

      <Table
        entity={{ singular: 'Supplemental Resource', plural: 'Supplemental Resources' }}
        enableTopToolbar={false}
        enableTableHead={!!data?.length}
        data={data ?? []}
        columns={resourceColumnDefs}
        rowActionsInclude={() => {
          return ['view', 'edit', 'delete']
        }}
        onRowActionClick={(action, row) => {
          if (action === 'edit') {
            stageResource(row.original)
            openResourceModal(false)
          } else if (action === 'delete') {
            deleteResource(row.original)
          } else if (action === 'view') {
            if (row.original.type === 'text') {
              const newWindow = window.open('', '_blank')
              if (newWindow) {
                newWindow.document.write(generatePreviewHtml(row.original.title, row.original.data))
                newWindow.document.close()
              } else {
                modals.open({
                  title: row.original.title || 'Text Preview',
                  size: 'lg',
                  children: (
                    <Box p="md">
                      <Text component="pre" style={{ whiteSpace: 'pre-wrap' }}>
                        {row.original.data}
                      </Text>
                    </Box>
                  ),
                  zIndex: NESTED_MODAL_Z_INDEX,
                })
              }
            } else {
              const previewLinkBase = row.original.type === 'url' ? '' : `${configStore.serverBaseUrl}/files/`
              window.open(`${previewLinkBase}${row.original.data}`, '_blank')
            }
          }
        }}
        rowActionsTooltipLabels={{
          view: `Preview resource`,
          edit: ' Edit or replace file',
          delete: 'Permanently delete',
        }}
        EmptyStateProps={{
          Icon: () => null,
          title: '',
          description: 'No supplemental resources added yet. Click "Add Resource" to upload.',
          bg: 'gray.0',
          style: {
            borderRadius: theme.radius.md,
          },
        }}
        displayColumnDefOptions={{ 'mrt-row-actions': { size: 138 } }}
        mantinePaperProps={{
          style: isMobile
            ? {
                overflowX: 'auto',
                width: 'calc(100vw - 6rem)',
                maxWidth: '100%',
              }
            : undefined,
        }}
        mantineTableProps={{
          style: isMobile
            ? {
                minWidth: '600px', // Ensure table has minimum width to trigger horizontal scroll
              }
            : undefined,
        }}
      />
    </Stack>
  )
}

const resourceColumnDefs: MRT_ColumnDef<GetResourcesResponse['0'] & { valid: boolean }>[] = [
  {
    accessorKey: 'title',
    header: 'Title',
    grow: false,
  },
  {
    id: 'type',
    accessorFn: row => row.type?.toUpperCase(),
    header: 'Type',
    size: 100,
    grow: false,
  },
  {
    accessorKey: 'description',
    header: 'Description',
    grow: true,
    Cell: props => (
      <Text size="sm" lineClamp={2}>
        {props.renderedCellValue}
      </Text>
    ),
  },
  {
    id: 'data',
    header: 'Resource',
    grow: true,
    Cell: props => {
      const { configStore } = useGlobalState()
      const type = props.row.original.type
      const previewLinkBase = type === 'url' ? '' : `${configStore.serverBaseUrl}/files/`

      if (type === 'text') {
        return (
          <Text size="sm" lineClamp={2} style={{ whiteSpace: 'pre-wrap' }}>
            {props.row.original.data}
          </Text>
        )
      } else {
        return (
          <Button
            rightSection={<IconExternalLink size={16} />}
            color="indigo.3"
            variant="filled"
            component="a"
            size="xs"
            tt="uppercase"
            target="_blank"
            href={`${previewLinkBase}${props.row.original.data}`}>
            Preview {type}
          </Button>
        )
      }
    },
  },
  {
    id: 'status',
    header: 'Status',
    grow: false,
    size: 158,
    Cell: props => {
      if (!props.row.original.valid) {
        return (
          <Badge color="red" variant="light">
            Incomplete
          </Badge>
        )
      } else if (props.row.original.isApproved === false) {
        return (
          <Badge color="red" variant="light">
            NEEDS APPROVAL
          </Badge>
        )
      } else {
        return (
          <Badge color="green" variant="light">
            Ready to Submit
          </Badge>
        )
      }
    },
  },
]
